///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Hierarchical clustering of the webcam catalog, computed once per sync
/// in geographic space. Tier 0 is every camera; each tier above merges
/// whatever lies within a doubling radius of the highest-ranked unclaimed
/// item (greedy radius clustering, the supercluster approach), so a tier's
/// membership depends only on the data, never on where the screen edges or
/// grid lines happen to fall. The cloud node picks a tier from the camera's
/// ground scale and the picture only changes at tier boundaries, with
/// parent links so children can grow out of (and collapse back into) their
/// cluster instead of blinking.
///
/// Everything lives in typed arrays: ~38k leaves plus the shrinking tiers
/// above them cost a few megabytes, nothing per camera is allocated, and the
/// per-frame culling loops touch contiguous memory.
///

import { wgs } from "@/earthbrowser/utils/geographic"
import type { WebcamCategory } from "@shared-types/webcam"

const cRad = Math.PI / 180
const cDeg = 180 / Math.PI
const cKmPerDegree = wgs.a * cRad

export const cCategoryByIndex: WebcamCategory[] = ["traffic", "scenic", "weather", "marine", "aviation", "wildlife"]
const cCategoryIndex = new Map<string, number>(cCategoryByIndex.map((c, i) => [c, i]))

/** Leaf flag bits */
export const cFlagStream = 1
export const cFlagStale = 2

/**
 * Merge radius per tier (km). Tier 0 is the raw catalog (nothing merged);
 * the top tier leaves a few dozen clusters for the whole globe.
 */
export const cTierRadiiKm = [0, 0.6, 1.2, 2.4, 4.8, 9.6, 19.2, 38.4, 76.8, 153.6, 307.2, 614.4, 1228.8]

// Lat/lon cells for viewport enumeration of a tier
const cCellDeg = 2
const cCellCols = 360 / cCellDeg
const cCellRows = 180 / cCellDeg
// Longitude cell span cap in the neighbor search near the poles
const cMaxLonSpan = 12

export interface WebcamCatalogLeaf {
	id: string
	lat: number
	lon: number
	category: WebcamCategory
	hasStream: boolean
	stale: boolean
}

/** One level of the hierarchy: items are cameras (tier 0) or clusters */
export class WebcamTier {
	public readonly count: number
	public readonly lat: Float32Array
	public readonly lon: Float32Array
	// Unit normal of the item on the sphere (horizon culling, placement)
	public readonly nx: Float32Array
	public readonly ny: Float32Array
	public readonly nz: Float32Array
	/** Cameras under this item (1 for a leaf or a lone camera passed up) */
	public readonly size: Uint32Array
	/** Highest-ranked camera under this item */
	public readonly leaf: Uint32Array
	/** Containing item in the next coarser tier, -1 at the top */
	public readonly parent: Int32Array
	/** Farthest member camera from the centroid (km), 0 for leaves */
	public readonly extentKm: Float32Array
	public readonly rank: Uint8Array
	// CSR buckets over the lat/lon cell grid
	public readonly cellOffsets = new Uint32Array(cCellCols * cCellRows + 1)
	public readonly cellItems: Uint32Array

	constructor(count: number) {
		this.count = count
		this.lat = new Float32Array(count)
		this.lon = new Float32Array(count)
		this.nx = new Float32Array(count)
		this.ny = new Float32Array(count)
		this.nz = new Float32Array(count)
		this.size = new Uint32Array(count)
		this.leaf = new Uint32Array(count)
		this.parent = new Int32Array(count).fill(-1)
		this.extentKm = new Float32Array(count)
		this.rank = new Uint8Array(count)
		this.cellItems = new Uint32Array(count)
	}

	/** Place an item; derives the unit normal from lat/lon (degrees) */
	public set(i: number, lat: number, lon: number, size: number, leaf: number, extentKm: number, rank: number): void {
		this.lat[i] = lat
		this.lon[i] = lon
		const cosLat = Math.cos(lat * cRad)
		this.nx[i] = Math.sin(lon * cRad) * cosLat
		this.ny[i] = Math.sin(lat * cRad)
		this.nz[i] = Math.cos(lon * cRad) * cosLat
		this.size[i] = size
		this.leaf[i] = leaf
		this.extentKm[i] = extentKm
		this.rank[i] = rank
	}

	/** Bucket the items by cell once every item is placed */
	public buildCells(): void {
		const offsets = this.cellOffsets
		offsets.fill(0)
		const cells = new Uint32Array(this.count)
		for (let i = 0; i < this.count; i++) {
			const cell = cellOf(this.lat[i], this.lon[i])
			cells[i] = cell
			offsets[cell + 1]++
		}
		for (let c = 1; c < offsets.length; c++) {
			offsets[c] += offsets[c - 1]
		}
		const cursor = offsets.slice(0, offsets.length - 1)
		for (let i = 0; i < this.count; i++) {
			this.cellItems[cursor[cells[i]]++] = i
		}
	}

	/**
	 * Append the indices of the items whose cell overlaps the bbox
	 * [minLon, minLat, maxLon, maxLat] (degrees; minLon > maxLon when it
	 * crosses the antimeridian). Cell-granular, so callers still cull.
	 */
	public collectInBBox(bbox: [number, number, number, number], out: number[]): void {
		const row0 = clampCell(Math.floor((bbox[1] + 90) / cCellDeg), cCellRows)
		const row1 = clampCell(Math.floor((bbox[3] + 90) / cCellDeg), cCellRows)
		const col0 = clampCell(Math.floor((bbox[0] + 180) / cCellDeg), cCellCols)
		const col1 = clampCell(Math.floor((bbox[2] + 180) / cCellDeg), cCellCols)
		if (col0 <= col1) {
			this.collectCells(row0, row1, col0, col1, out)
		} else {
			this.collectCells(row0, row1, col0, cCellCols - 1, out)
			this.collectCells(row0, row1, 0, col1, out)
		}
	}

	public collectAll(out: number[]): void {
		for (let i = 0; i < this.count; i++) out.push(i)
	}

	private collectCells(row0: number, row1: number, col0: number, col1: number, out: number[]): void {
		const offsets = this.cellOffsets
		const items = this.cellItems
		for (let row = row0; row <= row1; row++) {
			const base = row * cCellCols
			const start = offsets[base + col0]
			const end = offsets[base + col1 + 1]
			for (let k = start; k < end; k++) out.push(items[k])
		}
	}
}

function clampCell(c: number, n: number): number {
	return c < 0 ? 0 : c >= n ? n - 1 : c
}

function cellOf(lat: number, lon: number): number {
	const row = clampCell(Math.floor((lat + 90) / cCellDeg), cCellRows)
	const col = clampCell(Math.floor((lon + 180) / cCellDeg), cCellCols)
	return row * cCellCols + col
}

/**
 * Cell representative preference: live streams are rarer and richer than
 * snapshots, non-traffic categories are sparse enough that one shouldn't
 * vanish under a wall of traffic cams, and stale cams yield to healthy ones
 */
function leafRank(category: WebcamCategory, hasStream: boolean, stale: boolean): number {
	return (category !== "traffic" ? 4 : 0) + (hasStream ? 2 : 0) + (stale ? 0 : 1)
}

/**
 * Greedy radius clustering of `prev` into a coarser tier: visit items by
 * rank, and each unclaimed one claims every unclaimed item within the
 * radius. Clusters sit at the camera-weighted centroid of their members.
 */
function buildTier(prev: WebcamTier, radiusKm: number): WebcamTier {
	const n = prev.count
	const cosThreshold = Math.cos(radiusKm / wgs.a)
	// Neighbor hash grid with one merge radius per cell in latitude
	const cellDeg = radiusKm / cKmPerDegree
	const cols = Math.ceil(360 / cellDeg)
	const grid = new Map<number, number[]>()
	const latCells = new Int32Array(n)
	const lonCells = new Int32Array(n)
	for (let i = 0; i < n; i++) {
		const latCell = Math.floor((prev.lat[i] + 90) / cellDeg)
		const lonCell = ((Math.floor((prev.lon[i] + 180) / cellDeg) % cols) + cols) % cols
		latCells[i] = latCell
		lonCells[i] = lonCell
		const key = latCell * cols + lonCell
		const bucket = grid.get(key)
		if (bucket) bucket.push(i)
		else grid.set(key, [i])
	}

	const order = new Uint32Array(n)
	for (let i = 0; i < n; i++) order[i] = i
	order.sort((a, b) => (prev.rank[b] - prev.rank[a]) || (prev.size[b] - prev.size[a]) || (a - b))

	const assigned = new Int32Array(n).fill(-1)
	const outLat: number[] = []
	const outLon: number[] = []
	const outSize: number[] = []
	const outLeaf: number[] = []
	const outExtent: number[] = []
	const outRank: number[] = []
	const members: number[] = []

	for (let k = 0; k < n; k++) {
		const seed = order[k]
		if (assigned[seed] >= 0) continue
		const cluster = outLat.length
		assigned[seed] = cluster
		members.length = 0
		members.push(seed)

		const sx = prev.nx[seed]
		const sy = prev.ny[seed]
		const sz = prev.nz[seed]
		const latCell = latCells[seed]
		const lonCell = lonCells[seed]
		const cosLat = Math.max(0.05, Math.cos(prev.lat[seed] * cRad))
		const lonSpan = Math.min(cMaxLonSpan, Math.ceil(1 / cosLat))
		for (let dr = -1; dr <= 1; dr++) {
			const rowBase = (latCell + dr) * cols
			for (let dc = -lonSpan; dc <= lonSpan; dc++) {
				const bucket = grid.get(rowBase + (((lonCell + dc) % cols) + cols) % cols)
				if (!bucket) continue
				for (const j of bucket) {
					if (assigned[j] >= 0) continue
					if (sx * prev.nx[j] + sy * prev.ny[j] + sz * prev.nz[j] >= cosThreshold) {
						assigned[j] = cluster
						members.push(j)
					}
				}
			}
		}

		// Camera-weighted centroid, back to the sphere
		let cx = 0, cy = 0, cz = 0, total = 0
		for (const j of members) {
			const w = prev.size[j]
			cx += prev.nx[j] * w
			cy += prev.ny[j] * w
			cz += prev.nz[j] * w
			total += w
		}
		const len = Math.hypot(cx, cy, cz) || 1
		cx /= len
		cy /= len
		cz /= len
		let extent = 0
		for (const j of members) {
			const d = Math.acos(Math.min(1, Math.max(-1, cx * prev.nx[j] + cy * prev.ny[j] + cz * prev.nz[j]))) * wgs.a
			extent = Math.max(extent, prev.extentKm[j] + d)
		}

		outLat.push(Math.asin(Math.min(1, Math.max(-1, cy))) * cDeg)
		outLon.push(Math.atan2(cx, cz) * cDeg)
		outSize.push(total)
		outLeaf.push(prev.leaf[seed])
		outExtent.push(extent)
		outRank.push(prev.rank[seed])
	}

	const tier = new WebcamTier(outLat.length)
	for (let c = 0; c < outLat.length; c++) {
		tier.set(c, outLat[c], outLon[c], outSize[c], outLeaf[c], outExtent[c], outRank[c])
	}
	for (let i = 0; i < n; i++) prev.parent[i] = assigned[i]
	tier.buildCells()
	return tier
}

export class WebcamClusterIndex {
	public readonly tiers: WebcamTier[] = []
	public readonly ids: string[]
	public readonly category: Uint8Array
	public readonly flags: Uint8Array
	/** Wall time spent building, for diagnostics */
	public readonly buildMs: number

	constructor(leaves: WebcamCatalogLeaf[]) {
		const started = performance.now()
		const n = leaves.length
		this.ids = new Array<string>(n)
		this.category = new Uint8Array(n)
		this.flags = new Uint8Array(n)

		const base = new WebcamTier(n)
		for (let i = 0; i < n; i++) {
			const leaf = leaves[i]
			this.ids[i] = leaf.id
			this.category[i] = cCategoryIndex.get(leaf.category) ?? 1
			this.flags[i] = (leaf.hasStream ? cFlagStream : 0) | (leaf.stale ? cFlagStale : 0)
			base.set(i, leaf.lat, leaf.lon, 1, i, 0, leafRank(leaf.category, leaf.hasStream, leaf.stale))
		}
		base.buildCells()
		this.tiers.push(base)

		for (let t = 1; t < cTierRadiiKm.length; t++) {
			const tier = buildTier(this.tiers[t - 1], cTierRadiiKm[t])
			this.tiers.push(tier)
			// Nothing left to merge: the remaining tiers would be copies
			if (tier.count <= 1) break
		}
		this.buildMs = performance.now() - started
	}

	public get leafCount(): number {
		return this.tiers[0].count
	}

	public get tierCount(): number {
		return this.tiers.length
	}

	/** Index of the item in `targetTier` (>= tier) that contains item `index` of `tier` */
	public ancestor(tier: number, index: number, targetTier: number): number {
		let i = index
		for (let t = tier; t < targetTier && i >= 0; t++) {
			i = this.tiers[t].parent[i]
		}
		return i
	}

	/**
	 * Every camera under item `index` of `tier`, best-ranked first (rank,
	 * then id for a stable order). Walks each leaf's parent chain, so it
	 * costs a pass over the catalog; meant for a user action, not a frame
	 */
	public membersOf(tier: number, index: number): number[] {
		const leaves = this.tiers[0]
		if (tier === 0) return [index]
		const members: number[] = []
		for (let leaf = 0; leaf < leaves.count; leaf++) {
			if (this.ancestor(0, leaf, tier) === index) members.push(leaf)
		}
		members.sort((a, b) => (leaves.rank[b] - leaves.rank[a]) || (this.ids[a] < this.ids[b] ? -1 : 1))
		return members
	}

	/** Leaf index for a camera id, or -1 (lookup table built on first use) */
	public leafOf(id: string): number {
		if (!this.idIndex) {
			this.idIndex = new Map()
			for (let i = 0; i < this.ids.length; i++) this.idIndex.set(this.ids[i], i)
		}
		return this.idIndex.get(id) ?? -1
	}

	private idIndex?: Map<string, number>
}
